import React, { FC, useCallback, useEffect, useState } from "react";
import "@xyflow/react/dist/style.css";
import {
  NodeChange,
  applyNodeChanges,
  EdgeChange,
  applyEdgeChanges,
  useNodesInitialized,
  useReactFlow,
  ReactFlowProvider,
  Node,
  Edge,
} from "@xyflow/react";
import { StructuredRelatedCompanyDto } from "@green0meter/api-client";
import { useTranslation } from "react-i18next";

import { EntityEditor } from "../EntitiesManagement/components";
import OrgChart from "./OrgChart";
import { useAutoLayout } from "./hooks/useAutoLayout";
import { calculateDiagramNodes, calculateDiagramEdges } from "./OrgChart.helper";

import { Skeleton, Box, Button } from "@components/base";
import { useGetCompaniesStructure } from "@api";
import { useDisclose } from "@hooks/useDisclose";
import { useUserStore } from "@store/user";

const Diagram: FC<{
  data: StructuredRelatedCompanyDto;
  renderDiagram: number;
}> = ({ data, renderDiagram }) => {
  const [isDiagramReady, setIsDiagramReady] = useState(false);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const nodesInitialized = useNodesInitialized();
  const { fitView } = useReactFlow();

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [setNodes],
  );
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges],
  );

  useEffect(() => {
    const _nodes = calculateDiagramNodes(data, nodes);
    const _edges = calculateDiagramEdges(data);

    setNodes(_nodes);
    setEdges(_edges);
  }, [data]);

  useAutoLayout(renderDiagram, setIsDiagramReady);

  useEffect(() => {
    if (isDiagramReady && nodesInitialized) {
      setTimeout(() => {
        fitView({
          duration: 300,
          padding: data.childCompanies?.length ? 1 : 5,
        });
      });
    }
  }, [nodesInitialized, isDiagramReady]);

  return (
    <OrgChart
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
    />
  );
};

const OrgChartContainer: FC = () => {
  const {
    isOpen: isCreateEntityDialogOpen,
    onOpen: openCreateEntityDialog,
    onClose: closeCreateEntityDialog,
  } = useDisclose();
  const [renderDiagram, setRenderDiagram] = useState(0);

  const { t } = useTranslation();
  const { user } = useUserStore();
  const {
    companiesStructure,
    isPendingGetCompaniesStructure,
    refetchCompaniesStructure,
  } = useGetCompaniesStructure(user);

  const handleAddEntityButtonClick = (): void => {
    openCreateEntityDialog();
  };

  const handleCloseCreateEntityDialog = (): void => {
    closeCreateEntityDialog();
    refetchCompaniesStructure();
    setRenderDiagram((prev) => prev + 1);
  };

  const isVisibleSkeleton =
    isPendingGetCompaniesStructure ||
    !companiesStructure ||
    Object.keys(companiesStructure).length === 0;

  return (
    <>
      <EntityEditor
        isOpen={isCreateEntityDialogOpen}
        onClose={handleCloseCreateEntityDialog}
      />
      <Box mb={2} mt={3}>
        <Button onClick={handleAddEntityButtonClick} size="small">
          {t("organization.buttonAdd")}
        </Button>
      </Box>
      <ReactFlowProvider>
        {isVisibleSkeleton ? (
          <Skeleton variant="rectangular" height="100%" width="100%" />
        ) : (
          <Diagram data={companiesStructure} renderDiagram={renderDiagram} />
        )}
      </ReactFlowProvider>
    </>
  );
};

export default OrgChartContainer;
